import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { SharedModule } from '../../@commons/ui-components/shared.module';
import { Plant } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@entities/app/plant.entity';
import { PlantApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/location/plant/plant-api';
import { ActivatedRoute } from '@angular/router';
import {
  PlantResponse
} from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/location/plant.response';
import { NeedsComponent } from '../@components/needs/needs.component';
import { StorageService } from '../../@commons/@services/front/storage.service';
import { HexToHsl } from '../../@commons/pipes/HexToHsl';
import { CalendarPeriodLineComponent } from '../../calendar/@components/calendar-hierarchies/calendar-hierarchy-card/calendar-hierarchy-card-item/calendar-period-line/calendar-period-line.component';
import { CultivationPeriodType } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@enums/cultivation-period-type.enum';
import moment from 'moment';
import { CultivationPeriodListRequest } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/requests/cultivation/cultivation-period-list.request';
import { CultivationPeriodApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/cultivation/cultivation-period/cultivation-period-api';
import { CalendarMonthsHeaderComponent } from '../../calendar/@components/calendar-hierarchies/calendar-months-header/calendar-months-header.component';
import { CardBlockComponent } from '../../@commons/@components/cards/card-block/card-block.component';
import { CultivationRelationApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/cultivation/cultivation-relation/cultivation-relation-api';
import { CloseIconComponent } from '../../@icons/close-icon.component';
import { ArticleApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/article/article-api';
import { ArticleListResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/article/article-list.response';
import { ShopRedirectService } from '../../@commons/@services/front/shop-redirect.service';

@Component({
  selector: 'vw-cultivation-guide',
  imports: [
    SharedModule,
    CommonModule,
    TranslatePipe,
    NeedsComponent,
    HexToHsl,
    CalendarPeriodLineComponent,
    CalendarMonthsHeaderComponent,
    CardBlockComponent,
    CloseIconComponent,
  ],
  providers: [
    PlantApi,
    CultivationPeriodApi,
    StorageService,
    CultivationRelationApi,
    ArticleApi
  ],
  templateUrl: './cultivation-guide.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./cultivation-guide.component.scss']
})
export class CultivationGuideComponent {
  risks: any[] = []; // Holds the risks associated with the plant
  relations: { name: string, type: string }[] = []; // Holds the companion plants for the plant
  articles: ArticleListResponse[] = [];
  @Input() plant: Plant = null;
  @Input() cultivationVariant: any;
  @Input() cultivation: any;


  seedPeriods: { startMonth: number, endMonth: number }[] = [];
  harvestPeriods: { startMonth: number, endMonth: number }[] = [];

  constructor(private plantApi: PlantApi,
    private cultivationPeriodApi: CultivationPeriodApi,
    private cultivationRelationApi: CultivationRelationApi,
    private articleApi: ArticleApi,
    private route: ActivatedRoute,
    private storageService: StorageService,
    private shopRedirect: ShopRedirectService) { }

  /** Opens the webshop (external browser) to buy seeds of this cultivation. */
  onBuySeeds(): void {
    const cultivationUid = this.cultivation?.uid ?? this.cultivationVariant?.cultivation?.uid;
    this.shopRedirect.buyForCultivation(cultivationUid);
  }

  /**
   * Lifecycle hook that is called after the component is initialized.
   * Fetches plant details from the service.
   */
  async ngOnInit() {
    const plantUid = this.route.snapshot.paramMap.get('plantUid'); // Get the plant ID from route parameters
    await this.loadPlant(plantUid);
    //this.risks =  plant?.cultivationVariant?.risks//TODO
    //this.friends = plant?.cultivationVariant?.friends//TODO
    await this.loadPeriods();
    await this.loadAssociations();
  }

  async loadPlant(plantUid: string | null) {
    if (plantUid) {
      this.plantApi.find(plantUid, this.storageService.getCurrentLanguage()).then((res: PlantResponse) => {
        this.plant = res; // Assign the fetched plant details to the component property
      });
    }
  }

  /**
   * Method to load the cultivation periods for the variant.
   */
  async loadPeriods() {
    if (this.cultivationVariant) {
      const listCultivationPeriods: CultivationPeriodListRequest = new CultivationPeriodListRequest();
      listCultivationPeriods.cultivationVariantUid = this.cultivationVariant.uid;
      const periods = await this.cultivationPeriodApi.list(listCultivationPeriods, 0, 20, this.storageService.getCurrentLanguage());
      const mappedPeriods = periods.results.map(period => ({
        startMonth: moment(period.startDate).month() + 1,
        endMonth: moment(period.endDate).month() + 1,
        type: period.type
      }));

      this.seedPeriods = mappedPeriods.filter(period => period.type === CultivationPeriodType.SEEDLING);
      this.harvestPeriods = mappedPeriods.filter(period => period.type === CultivationPeriodType.HARVEST);
    }
  }

  async loadAssociations() {
    if (this.cultivation) {
      const relations = await this.cultivationRelationApi.list({ cultivationUid: this.cultivation.uid }, 0, 20, this.storageService.getCurrentLanguage());
      this.relations = relations.results.map(relation => ({
        name: relation.cultivation1.uid === this.cultivation.uid ? relation.cultivation2.name.toString() : relation.cultivation1.name.toString(),
        type: relation.relationType
      })).sort((a, b) => a.type === 'FRIENDS' ? -1 : 1);
    }
  }

  getFamily() {
    const hierarchy = this.cultivation.cultivationHierarchy;
    if (hierarchy.type === 'FAMILY') {
      return hierarchy.name;
    } else if (hierarchy.parent.type === 'FAMILY') {
      return hierarchy.parent.name;
    } else if (hierarchy.parent.parent) {
      return hierarchy.parent.parent.name;
    } else {
      return '';
    }
  }
}
